import axios from '@/axiosInterceptor';
import {
  defaultTabFilters,
  defaultTabSort,
  defaultPagination,
  sortParamLookup,
  filterParamLookup,
} from '@/utils/myLoadsFiltersLookup.js';

// helpers

const buildFilterParams = (query) => {
  let filterCount = 0;
  let params = '';
  // console.log('query', query);
  for (const key in query) {
    if (
      key === 'carrierIdType' ||
      key === 'originType' ||
      key === 'destinationType' ||
      key === 'sort' ||
      key === 'page' ||
      key.toLowerCase().includes('input')
    ) {
      continue;
    }
    if (key === 'tab') {
      params += defaultTabFilters[query[key]];
    } else if (
      key === 'equipment' &&
      Array.isArray(query[key]) &&
      query[key].length > 0
    ) {
      query[key].forEach((equipmentType) => {
        params += filterParamLookup[key].query + equipmentType;
        filterCount++;
      });
    } else if (query[key] && filterParamLookup[key]) {
      filterCount++;
      params += filterParamLookup[key].query + query[key];
    }
  }
  return { filterCount: filterCount, params: params };
};

const formatPagination = ({
  currentpage = 1,
  totalpages = 0,
  totalitems = 0,
}) => ({
  currentPage: Number(currentpage),
  totalPages: Number(totalpages),
  totalRecords: Number(totalitems),
});

const formatVehicles = (vehicles) => {
  const formattedVehicles = [];
  if (vehicles.length) {
    vehicles.forEach((vehicle) => {
      formattedVehicles.push({
        id: vehicle.carrier_vehicle_id,
        identifier: vehicle.truck_number || '',
        vin: vehicle.vin || '',
        source: vehicle.source,
        isEldActive: vehicle.is_eld_and_active,
        isEldExempt: vehicle.is_eld_exempt,
        eldExemptCategory: vehicle.eld_exemption_type,
        isUnverified: !vehicle.is_coi,
      });
    });
  }
  return formattedVehicles;
};

const state = () => ({
  showConnectEldDialog: false,
  all: {
    hasLoaded: false,
    isLoading: false,
    loads: [],
    pagination: defaultPagination,
  },
  unassigned: {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    loads: [],
    pagination: defaultPagination,
    activeFiltersCount: 0,
    query: {
      tab: 'unassigned',
      sort: defaultTabSort.unassigned,
      page: 1,
    },
  },
  dispatched: {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    loads: [],
    pagination: defaultPagination,
    activeFiltersCount: 0,
    query: {
      tab: 'dispatched',
      sort: defaultTabSort.dispatch,
      page: 1,
    },
  },
  'in-transit': {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    loads: [],
    pagination: defaultPagination,
    activeFiltersCount: 0,
    query: {
      tab: 'in-transit',
      sort: defaultTabSort['in-transit'],
      page: 1,
    },
  },
  completed: {
    controller: null,
    hasLoaded: false,
    isLoading: false,
    loads: [],
    pagination: defaultPagination,
    activeFiltersCount: 0,
    query: {
      tab: 'completed',
      sort: defaultTabSort.completed,
      page: 1,
    },
  },
  vehicles: {
    controller: null,
    pagination: defaultPagination,
    isLoading: false,
    hasLoaded: false,
    allVehicles: [],
    eldVehicles: [],
  },
  equipmentTableVehicles: {
    controller: null,
    pagination: defaultPagination,
    isLoading: false,
    hasLoaded: false,
    vehicles: [],
    eldStatus: '',
  },
  brokers: {
    controller: null,
    pagination: defaultPagination,
    isLoading: false,
    hasLoaded: false,
    allBrokers: [],
  },
  drivers: {
    controller: null,
    pagination: defaultPagination,
    isLoading: false,
    hasLoaded: false,
    allDrivers: [],
  },
  equipmentCount: null,
});
const mutations = {
  setEquipmentCount(state, equipmentCount) {
    state.equipmentCount = equipmentCount;
  },
  setAllDrivers(state, drivers) {
    state.drivers.allDrivers = drivers;
  },
  setShowConnectEldDialog(state, bool) {
    state.showConnectEldDialog = bool;
  },
  setHasLoaded(state, { tab, bool }) {
    state[tab].hasLoaded = bool;
  },
  setIsLoading(state, { tab, bool }) {
    state[tab].isLoading = bool;
  },
  setController(state, { tab, controller }) {
    state[tab].controller = controller;
  },
  setLoads(state, { tab, loads }) {
    state[tab].loads = loads;
  },
  setPagination(state, { tab, pagination }) {
    state[tab].pagination = pagination;
  },
  setQuery(state, { tab, query }) {
    state[tab].query = query;
  },
  setAllVehicles(state, allVehicles) {
    state.vehicles.allVehicles = allVehicles;
  },
  setEquipmentTableVehicles(state, equipmentTableVehicles) {
    state.equipmentTableVehicles.vehicles = equipmentTableVehicles;
  },
  setEquipmentTableEldStatus(state, eldStatus) {
    state.equipmentTableVehicles.eldStatus = eldStatus;
  },
  setEldVehicles(state, eldVehicles) {
    state.vehicles.eldVehicles = eldVehicles;
  },
  setAllBrokers(state, allBrokers) {
    state.brokers.allBrokers = allBrokers;
  },
  setFilter(state, { filter, value }) {
    state.filters[filter] = value;
  },
  setFilters(state, filters) {
    state.filters = filters;
  },
  setFilterCount(state, { tab, value }) {
    state[tab].activeFiltersCount = value;
  },
};
// actions
const actions = {
  setFilters({ commit }, filters) {
    commit('setFilters', filters);
  },
  setFilter({ commit }, { filter, value }) {
    commit('setFilter', { filter: filter, value: value });
  },
  saveQuery({ commit }, { tab, query }) {
    commit('setQuery', { tab: tab, query: query });
  },
  async getMyLoads({ commit, state }, tab = 'unassigned') {
    const query = state[tab].query;
    const { page = 1, sort = 'id+desc' } = query;
    // console.log('getMyLoads query', query);
    const sortParam =
      sortParamLookup[sort] || query.pickupEnd
        ? 'start_stop_scheduled_window_started_at+asc'
        : 'id+desc';
    // build ransanck params from query
    const filterParams = buildFilterParams(query);
    // console.log('filterParams', filterParams);
    commit('setFilterCount', { tab: tab, value: filterParams.filterCount });
    const url = `${process.env.API_MONITOR}/api/v1/loads/carrier_index?${filterParams.params}&q[s]=${sortParam}&page=${page}&per_page=50`;

    // if prev call, abort it
    const previousRequest = state[tab].controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', { tab: tab, controller: null });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: tab, bool: false });
    commit('setIsLoading', { tab: tab, bool: true });
    commit('setController', { tab: tab, controller: controller });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data && response?.headers?.currentpage) {
        commit('setQuery', { tab: tab, query: query });
        commit('setController', { tab: tab, controller: null });
        commit('setIsLoading', { tab: tab, bool: false });
        commit('setHasLoaded', { tab: tab, bool: true });
        commit('setLoads', { tab: tab, loads: response.data });
        commit('setPagination', {
          tab: tab,
          pagination: formatPagination(response.headers),
        });
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: tab, bool: false });
      commit('setHasLoaded', { tab: tab, bool: true });
      commit('setLoads', { tab: tab, loads: [] });
      commit('setPagination', { tab: tab, pagination: defaultPagination });
      return error;
    }
  },
  async getAllMyLoads({ commit }, tab = 'all') {
    const url = `${process.env.API_MONITOR}/api/v1/loads/carrier_index`;
    commit('setHasLoaded', { tab: tab, bool: false });
    commit('setIsLoading', { tab: tab, bool: true });
    try {
      const response = await axios.get(url);
      if (response?.data && response?.headers?.currentpage) {
        commit('setIsLoading', { tab: tab, bool: false });
        commit('setHasLoaded', { tab: tab, bool: true });
        commit('setLoads', { tab: tab, loads: response.data });
        commit('setPagination', {
          tab: tab,
          pagination: formatPagination(response.headers),
        });
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: tab, bool: false });
      commit('setHasLoaded', { tab: tab, bool: true });
      commit('setLoads', { tab: tab, loads: [] });
      commit('setPagination', { tab: tab, pagination: defaultPagination });
      return error;
    }
  },
  //   circle back to this later
  async getMyLoad({ commit }, { id, query = '' }) {
    commit('setLoadDetailsLoading', true);
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/loads/${id}/${query}`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setLoadDetail', res.data);
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      commit('setLoadDetailsLoading', false);
    }
  },
  async patchMyLoad(_, options) {
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/loads/${options.id}/carrier_update`,
        options.payload
      );
      return res;
    } catch (error) {
      console.error('error', error);
      return error;
    }
  },
  async postCarrierVehicleEldExemptStatus({ rootState }, options) {
    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/carriers/${rootState.users.currentUser?.company?.carrier_id}/carrier_set_carrier_vehicle_eld_exempt_status`,
        options.payload
      );
      return res;
    } catch (error) {
      console.error('error', error);
      return error;
    }
  },
  async patchCloseAlert(_, id) {
    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/tickets/${id}/close_ticket_by_carrier_user`
      );
      return res;
    } catch (error) {
      console.error('error', error);
      return error;
    }
  },
  async getEquipmentTableCarrierVehicles(
    { commit, state, rootState },
    options = {}
  ) {
    const { resultsPerPage = 50, page = 1 } = options;
    const url = `${process.env.API_MONITOR}/api/v1/carriers/${rootState.users.currentUser?.company?.carrier_id}/carrier_vehicles_for_carrier?&page=${page}&per_page=${resultsPerPage}`;
    const previousRequest = state.vehicles.controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', {
        tab: 'equipmentTableVehicles',
        controller: null,
      });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: 'equipmentTableVehicles', bool: false });
    commit('setIsLoading', { tab: 'equipmentTableVehicles', bool: true });
    commit('setController', {
      tab: 'equipmentTableVehicles',
      controller: controller,
    });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data && response?.headers?.currentpage) {
        commit('setController', {
          tab: 'equipmentTableVehicles',
          controller: null,
        });
        commit('setIsLoading', { tab: 'equipmentTableVehicles', bool: false });
        commit('setHasLoaded', { tab: 'equipmentTableVehicles', bool: true });
        commit('setEquipmentTableVehicles', response.data?.vehicles || []);
        commit('setEquipmentTableEldStatus', response.data?.eld_status || '');
        commit('setEquipmentCount', response.data?.equipment_count || null);
        commit('setPagination', {
          tab: 'equipmentTableVehicles',
          pagination: formatPagination(response.headers),
        });
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: 'equipmentTableVehicles', bool: false });
      commit('setHasLoaded', { tab: 'equipmentTableVehicles', bool: true });
      commit('setEquipmentTableVehicles', []);
      commit('setEquipmentTableEldStatus', '');
      commit('setEquipmentCount', null);
      commit('setPagination', {
        tab: 'equipmentTableVehicles',
        pagination: defaultPagination,
      });
      return error;
    }
  },
  async getCarrierVehicles({ commit, state, rootState }, options = {}) {
    const { resultsPerPage = 500, page = 1, loadId = '' } = options;
    let loadIdParam = '';
    if (loadId) {
      loadIdParam = `&load_id=${loadId}`;
    }
    const url = `${process.env.API_MONITOR}/api/v1/carriers/${rootState.users.currentUser?.company?.carrier_id}/carrier_vehicles_for_carrier?&page=${page}&per_page=${resultsPerPage}${loadIdParam}`;
    const previousRequest = state.vehicles.controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', { tab: 'vehicles', controller: null });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: 'vehicles', bool: false });
    commit('setIsLoading', { tab: 'vehicles', bool: true });
    commit('setController', { tab: 'vehicles', controller: controller });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data && response?.headers?.currentpage) {
        commit('setController', { tab: 'vehicles', controller: null });
        commit('setIsLoading', { tab: 'vehicles', bool: false });
        commit('setHasLoaded', { tab: 'vehicles', bool: true });
        commit('setAllVehicles', formatVehicles(response.data?.vehicles || []));
        commit(
          'setEldVehicles',
          formatVehicles(
            response.data?.vehicles.filter(
              (vehicle) => vehicle.is_eld_and_active
            ) || []
          )
        );
        commit('setPagination', {
          tab: 'vehicles',
          pagination: formatPagination(response.headers),
        });
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: 'vehicles', bool: false });
      commit('setHasLoaded', { tab: 'vehicles', bool: true });
      commit('setAllVehicles', []);
      commit('setEldVehicles', []);
      commit('setPagination', {
        tab: 'vehicles',
        pagination: defaultPagination,
      });
      return error;
    }
  },
  async getCarrierBrokers({ commit, state }) {
    const url = `${process.env.API_MONITOR}/api/v1/loads/carrier_brokers?&page=1&per_page=500`;
    const previousRequest = state.brokers.controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', { tab: 'brokers', controller: null });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: 'brokers', bool: false });
    commit('setIsLoading', { tab: 'brokers', bool: true });
    commit('setController', { tab: 'brokers', controller: controller });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data) {
        commit('setController', { tab: 'brokers', controller: null });
        commit('setIsLoading', { tab: 'brokers', bool: false });
        commit('setHasLoaded', { tab: 'brokers', bool: true });
        commit('setAllBrokers', response.data);
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: 'brokers', bool: false });
      commit('setHasLoaded', { tab: 'brokers', bool: true });
      commit('setAllBrokers', []);
      return error;
    }
  },
  async getDrivers({ commit, state, rootState }) {
    const url = `${process.env.API_MONITOR}/api/v1/carriers/${rootState.users.currentUser?.company?.carrier_id}/driver_users_for_carrier?&page=1&per_page=500`;
    const previousRequest = state.drivers.controller;
    if (previousRequest) {
      previousRequest.abort();
      commit('setController', { tab: 'drivers', controller: null });
    }
    const controller = new AbortController();
    commit('setHasLoaded', { tab: 'drivers', bool: false });
    commit('setIsLoading', { tab: 'drivers', bool: true });
    commit('setController', { tab: 'drivers', controller: controller });
    try {
      const response = await axios.get(url, {
        signal: controller.signal,
      });
      if (response?.data?.driver_users) {
        commit('setController', { tab: 'drivers', controller: null });
        commit('setIsLoading', { tab: 'drivers', bool: false });
        commit('setHasLoaded', { tab: 'drivers', bool: true });
        commit('setAllDrivers', response.data.driver_users);
        return response;
      }
      if (response) {
        throw new Error('No data returned from API', { cause: response });
      }
    } catch (error) {
      console.error(error);
      commit('setIsLoading', { tab: 'drivers', bool: false });
      commit('setHasLoaded', { tab: 'drivers', bool: true });
      commit('setAllDrivers', []);
      return error;
    }
  },
  async addDriverToLoad(_, options = {}) {
    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/loads/${options.id}/set_driver_user`,
        options.payload
      );
      return res;
    } catch (error) {
      console.error(error);
      return error;
    }
  },
  async addDriverInvitationToLoad(_, options = {}) {
    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/loads/${options.id}/set_driver_user_invitation`,
        options.payload
      );
      return res;
    } catch (error) {
      console.error(error);
      return error;
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
